/**
 * Meter Value Types
 * Types for meter values data in session reports
 * Based on Flutter MeterValueDetailItem model
 */

/**
 * API response model for meter value details
 * Matches the BackOffice API /actor/metervaluesdetailsbysession/{sessionId} response
 */
export interface MeterValueDetailApiModel {
  sessionId: number | null;
  meterValueId: number | null;
  timeStamp: string | null;           // UTC ISO string
  machineTimeStamp: string | null;    // Machine local time ISO string
  readingContexts: string | null;     // ReadingContext enum value
  energy: number | null;              // kWh
  deltaEnergy: number | null;
  power: number | null;
  current: string | null;
  voltage: string | null;
  temperature: number | null;
  stateOfCharge: number | null;
  taarifName: string | null;          // Note: typo in API, matches Flutter
  unitCost: number | null;
  cost: number | null;                // excl VAT
  vatCost: number | null;
  totalCost: number | null;           // incl VAT
  vatRate: number | null;
  hasDetails: boolean;
  isVirtualMeter: boolean | null;     // Virtual meter = highlighted row
}

/**
 * Grid row for display in the MeterValuesGrid
 * Transformed from API model for UI consumption
 */
export interface MeterValueGridRow {
  meterValueId: number;
  sessionId: number;
  timestamp: string;                  // Formatted for display (user time)
  machineTimestamp: string;           // Formatted for display (machine time)
  readingContext: string;
  energy: number | null;
  deltaEnergy: number | null;
  power: number | null;
  current: string | null;
  voltage: string | null;
  temperature: number | null;
  stateOfCharge: number | null;
  tariffName: string | null;
  unitCost: number | null;
  costExclVat: number | null;
  vatCost: number | null;
  costInclVat: number | null;
  vatRate: number | null;
  isVirtualMeter: boolean;
}

/**
 * Request parameters for fetching meter values
 */
export interface MeterValuesRequest {
  $skip?: number;
  $top?: number;
  $orderby?: string;
  $filter?: string;
  filterEmptyDelta?: boolean;
  timeZoneOffset?: number;            // Minutes offset for user time conversion
  count?: boolean;
  addVat?: boolean;       // Include VAT breakdown columns (VatCost, EstimatedPrice)
  addCost?: boolean;      // Include any cost columns (sell or buy)
  addBuyCost?: boolean;   // Include buy cost columns (operator cost)
  addSellCost?: boolean;  // Include sell cost columns (customer-facing fees)
}

/**
 * Reading context enum values
 * Matches OCPP ReadingContext enum
 */
export enum ReadingContext {
  Interruption_Begin = 'Interruption.Begin',
  Interruption_End = 'Interruption.End',
  Other = 'Other',
  Sample_Clock = 'Sample.Clock',
  Sample_Periodic = 'Sample.Periodic',
  Transaction_Begin = 'Transaction.Begin',
  Transaction_End = 'Transaction.End',
  Trigger = 'Trigger',
}

/**
 * Get display label for reading context
 */
export function getReadingContextLabel(context: string | null): string {
  if (!context) return '--';

  const labels: Record<string, string> = {
    'Interruption.Begin': 'Interruption Begin',
    'Interruption.End': 'Interruption End',
    'Other': 'Other',
    'Sample.Clock': 'Sample Clock',
    'Sample.Periodic': 'Sample Periodic',
    'Transaction.Begin': 'Transaction Begin',
    'Transaction.End': 'Transaction End',
    'Trigger': 'Trigger',
  };

  return labels[context] || context;
}
